var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getRuntimeEnv } from '../runtimeEnv';
import { parseIceServers, toSameOriginGo2rtcUrl } from './go2rtcUrl';
/**
 * Minimal WebRTC player for go2rtc `/api/webrtc?src=...` SDP exchange.
 * Same PlayerHandle surface as HJPlayer wrapper.
 */
export function createWebRtcPlayer(playback) {
    let video = null;
    let pc = null;
    const listeners = {
        MEDIA_INFO: [],
        ERROR: [],
    };
    let closed = false;
    const emit = (event, ...args) => {
        (listeners[event] || []).forEach((listener) => listener(...args));
    };
    const cleanupPc = () => {
        if (pc) {
            try {
                pc.getSenders().forEach((sender) => sender.track && sender.track.stop());
                pc.getReceivers().forEach((receiver) => receiver.track && receiver.track.stop());
                pc.close();
            }
            catch (_a) {
                // ignore
            }
            pc = null;
        }
        if (video) {
            video.srcObject = null;
        }
    };
    const handle = {
        attachMediaElement(element) {
            video = element;
            video.muted = true;
            video.autoplay = true;
            video.playsInline = true;
        },
        detachMediaElement() {
            if (video) {
                video.srcObject = null;
            }
            video = null;
        },
        load() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (!video || closed) {
                    return;
                }
                cleanupPc();
                try {
                    const iceServers = parseIceServers(getRuntimeEnv().MEDIA_WEBRTC_ICE_URLS);
                    pc = new RTCPeerConnection({ iceServers });
                    pc.addTransceiver('video', { direction: 'recvonly' });
                    pc.addTransceiver('audio', { direction: 'recvonly' });
                    pc.ontrack = (event) => {
                        var _a;
                        if (!video) {
                            return;
                        }
                        if (video.srcObject !== event.streams[0]) {
                            video.srcObject = event.streams[0];
                        }
                        (_a = video.play()) === null || _a === void 0 ? void 0 : _a.catch(() => undefined);
                        emit('MEDIA_INFO');
                    };
                    const onLoaded = () => {
                        emit('MEDIA_INFO');
                    };
                    if (video) {
                        video.addEventListener('loadeddata', onLoaded, { once: true });
                    }
                    pc.onconnectionstatechange = () => {
                        if (!pc) {
                            return;
                        }
                        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
                            emit('ERROR', 'NETWORK_ERROR');
                        }
                    };
                    const offer = yield pc.createOffer();
                    yield pc.setLocalDescription(offer);
                    // With STUN, gathering needs more time than LAN-only host candidates.
                    yield waitIceGathering(pc, iceServers.length ? 2500 : 200);
                    const sdpBody = ((_a = pc.localDescription) === null || _a === void 0 ? void 0 : _a.sdp) || offer.sdp || '';
                    const signalingUrl = toSameOriginGo2rtcUrl(playback.url);
                    let response = yield fetch(signalingUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/sdp' },
                        body: sdpBody,
                    });
                    // go2rtc / upstream occasionally 500 under parallel preview; one retry helps.
                    if (!response.ok && response.status >= 500) {
                        yield new Promise((resolve) => setTimeout(resolve, 400));
                        response = yield fetch(signalingUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/sdp' },
                            body: sdpBody,
                        });
                    }
                    if (!response.ok) {
                        emit('ERROR', 'NETWORK_ERROR');
                        return;
                    }
                    const answer = yield response.text();
                    yield pc.setRemoteDescription({ type: 'answer', sdp: answer });
                }
                catch (_b) {
                    emit('ERROR', 'MEDIA_ERROR');
                }
            });
        },
        unload() {
            closed = true;
            cleanupPc();
        },
        pause() {
            video === null || video === void 0 ? void 0 : video.pause();
        },
        on(event, callback) {
            if (!listeners[event]) {
                listeners[event] = [];
            }
            listeners[event].push(callback);
        },
        off() {
            Object.keys(listeners).forEach((key) => {
                listeners[key] = [];
            });
        },
    };
    return handle;
}
function waitIceGathering(pc, timeoutMs = 600) {
    if (pc.iceGatheringState === 'complete') {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(), timeoutMs);
        pc.onicegatheringstatechange = () => {
            if (pc.iceGatheringState === 'complete') {
                clearTimeout(timer);
                resolve();
            }
        };
    });
}
let hevcCapabilityCache = null;
/**
 * Runtime check: can this browser negotiate H.265 over WebRTC at all?
 * Best-effort; negotiation may still fail later.
 */
export function canAttemptHevcWebRtc() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!hevcCapabilityCache) {
            hevcCapabilityCache = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (typeof RTCRtpReceiver === 'undefined' || typeof RTCRtpReceiver.getCapabilities !== 'function') {
                        return false;
                    }
                    const caps = RTCRtpReceiver.getCapabilities('video');
                    const codecs = (caps === null || caps === void 0 ? void 0 : caps.codecs) || [];
                    return codecs.some((codec) => /hvc1|hev1|h265|hevc/i.test(codec.mimeType || ''));
                }
                catch (_a) {
                    return false;
                }
            }))();
        }
        return hevcCapabilityCache;
    });
}
